import React, { useMemo, useCallback } from "react";
import { FC } from "react";
import { Field } from "react-final-form";
import { FormRenderProps, useFormState, useForm } from "react-final-form";
import { Theme } from "@material-ui/core";
import { Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/core";
import Hidden from "@material-ui/core/Hidden";
import Divider from "@material-ui/core/Divider";
import Typography from "@material-ui/core/Typography";
import { getData } from "country-list";
import UploadFile from "../../../../components/UploadFile/UploadFile";
import BootstrapInput from "../../../../components/BootstrapInput";
import PasswordInput from "../../../../components/PasswordInput";
import Autocomplete from "../../../../components/Autocomplete";
import Loading from "../../../../components/Loading";
import Button from "../../../../components/Button";
import TermsApproval from "../TermsApproval";

const useStyles = makeStyles((theme: Theme) => ({
  fieldsContainer: {
    marginBottom: -4,
    "& .MuiGrid-item": {
      padding: 8,
    },
  },
  divider: {
    marginTop: 24,
    marginBottom: 24,
  },
  recaptchaText: {
    marginTop: 8,
  },
}));

const RegisterServiceProviderForm: FC<FormRenderProps> = ({
  handleSubmit,
  submitError,
  submitting,
}) => {
  const classes = useStyles();

  const { values } = useFormState();
  const { change } = useForm();

  const countries = useMemo(
    () =>
      getData().sort((a, b) =>
        a.name > b.name ? 1 : b.name > a.name ? -1 : 0,
      ),
    [],
  );

  const files = useMemo(
    () => (values.image ? [values.image] : []),
    [values.image],
  );

  const addNewFiles = useCallback(
    async (files) => {
      const file = files[0];

      change("image", {
        id: 1,
        name: file.name,
        size: file.size,
        file,
      });
    },
    [change],
  );

  const removeFileHandler = useCallback(() => {
    change("image", null);
  }, [change]);

  return (
    <form noValidate onSubmit={handleSubmit}>
      {submitError && <Typography color="error">{submitError}</Typography>}
      {submitting && <Loading />}
      <Grid container spacing={3} className={classes.fieldsContainer}>
        <Grid item xs={12} sm={6} md={4}>
          <Field
            required
            fullWidth
            type="text"
            name="serviceProviderProfile.serviceProvider.name"
            label="Service Provider Name"
            placeholder="Service Provider Name"
            component={BootstrapInput}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Field
            required
            fullWidth
            name="serviceProviderProfile.serviceProvider.country"
            label="Country"
            placeholder="Please Select"
            options={countries}
            component={Autocomplete}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Field
            required
            fullWidth
            type="text"
            name="serviceProviderProfile.serviceProvider.website"
            label="Service Provider Website"
            placeholder="www.edu.uk"
            component={BootstrapInput}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Field
            required
            fullWidth
            type="text"
            name="email"
            label="Service Provider Email"
            placeholder="example@test.com"
            component={BootstrapInput}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Field
            required
            fullWidth
            type="text"
            name="phone"
            label="Service Provider Phone Number"
            placeholder="+19159969739"
            component={BootstrapInput}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Field
            required
            fullWidth
            type="text"
            name="name"
            label="Representative Name"
            placeholder="John Smith"
            infoIcon={
              "To write the Representative Name of the person who is responsible for this account"
            }
            component={BootstrapInput}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Field
            required
            fullWidth
            name="password"
            label="Password"
            placeholder="Must have at least 8 characters"
            autoComplete="new-password"
            component={PasswordInput}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Field
            required
            fullWidth
            label="Repeat Password"
            placeholder="Must have at least 8 characters"
            name="password_confirmation"
            component={PasswordInput}
          />
        </Grid>
      </Grid>
      <Hidden smDown>
        <Divider className={classes.divider} />
      </Hidden>
      <Grid container spacing={3} alignItems={"center"} justify={"flex-end"}>
        <Grid item sm={4} xs={12}>
          <UploadFile
            required
            uploadedFiles={files}
            addNewFiles={addNewFiles}
            removeFile={removeFileHandler}
            titleText={"Service Provider Logo"}
            subText="Drag & Drop a service provider logo"
            allowedFileTypes={["jpg", "jpeg", "png"]}
            maxSize={5}
          />
        </Grid>
      </Grid>
      <Grid container spacing={3} alignItems={"center"} justify={"flex-end"}>
        <Grid item sm={4} xs={12}>
          <TermsApproval termsText={""} />
          <Button
            fullWidth
            type="submit"
            color="secondary"
            variant="contained"
            disabled={submitting}
          >
            Sign Up
          </Button>
        </Grid>
      </Grid>
      <Grid container spacing={3} alignItems={"center"} justify={"flex-end"}>
        <Grid item sm={4} xs={12}>
          <Typography
            component="p"
            align="center"
            className={classes.recaptchaText}
          >
            This site is protected by reCAPTCHA and the Google{" "}
            <a href="https://policies.google.com/privacy">Privacy Policy</a> and{" "}
            <a href="https://policies.google.com/terms">Terms of Service</a>{" "}
            apply.
          </Typography>
        </Grid>
      </Grid>
    </form>
  );
};

export default RegisterServiceProviderForm;
